// frontend/src/modules/reports/pages/workforce/IdleBreaksPage.tsx
//
// Phase 4 (Reports Frontend), Task 3. Per-employee break vs. idle minutes —
// follows the HoursSummaryPage reference pattern: ReportShell owns date
// range + CSV chrome, this page owns fetching (useApi keyed on the resolved
// range + employee filter), stat tiles, a per-employee TwinProgressBars
// visual (existing design-system chart primitive), and an exact-numbers
// table (counts + durations + active minutes).

import * as React from 'react';
import { Coffee, PauseCircle, TimerReset } from 'lucide-react';
import { ReportShell, type ReportRange } from '../../components/ReportShell';
import { StatCard } from '../../../../design-system/components/StatCard';
import { DataTable } from '../../../../design-system/components/DataTable';
import { TwinProgressBars } from '../../../../design-system/charts';
import { useApi } from '../../../../design-system/useApi';
import { reportUrl, downloadReportCsv } from '../../api';
import type { IdleBreaksReport } from '../../types';
import { VISUAL } from '../../../../design-system/visual';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../../components/ui/select';
import type { EmployeeWithUser } from '../../../hr/types';

/** '125' minutes -> '2h 5m'. */
function fmtMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function IdleBreaksPage() {
  const [range, setRange] = React.useState<ReportRange | null>(null);
  const [employeeId, setEmployeeId] = React.useState<string>('all');

  const empResult = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const employees = empResult.data?.employees ?? [];

  const path = range
    ? reportUrl('idle-breaks', {
      from: range.from,
      to: range.to,
      employeeId: employeeId !== 'all' ? employeeId : undefined,
    })
    : null;
  const result = useApi<{ report: IdleBreaksReport }>(path, [employeeId]);
  const forbidden = result.status === 403;

  const report = result.data?.report ?? null;
  const rows = report?.rows ?? [];

  const totals = React.useMemo(() => ({
    breakMinutes: rows.reduce((s, r) => s + r.break_minutes, 0),
    idleMinutes: rows.reduce((s, r) => s + r.idle_minutes, 0),
    activeMinutes: rows.reduce((s, r) => s + r.active_minutes, 0),
  }), [rows]);

  return (
    <ReportShell
      eyebrow="Reports / Workforce"
      title="Idle & breaks"
      description="Break and idle time per employee, alongside active minutes."
      onRangeChange={setRange}
      access={!forbidden}
      accessDescription={forbidden ? (result.error ?? undefined) : undefined}
      loading={!forbidden && (range === null || result.loading)}
      error={forbidden ? null : result.error}
      onRetry={result.reload}
      isEmpty={!forbidden && range !== null && !result.loading && !result.error && rows.length === 0}
      filters={
        employees.length > 0 && (
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger className="h-9 rounded-full bg-card/80 border-border/60 px-3.5 text-[12.5px] font-semibold w-auto min-w-[180px]">
              <SelectValue placeholder="All employees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )
      }
      csv={range ? {
        filename: `idle-breaks-${range.from}-to-${range.to}.csv`,
        onDownload: () => downloadReportCsv(
          'idle-breaks',
          { from: range.from, to: range.to, employeeId: employeeId !== 'all' ? employeeId : undefined },
          `idle-breaks-${range.from}-to-${range.to}.csv`,
        ),
      } : null}
    >
      {report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label="Break time" value={fmtMinutes(totals.breakMinutes)} icon={<Coffee />} />
            <StatCard label="Idle time" value={fmtMinutes(totals.idleMinutes)} icon={<PauseCircle />} />
            <StatCard label="Active time" value={fmtMinutes(totals.activeMinutes)} icon={<TimerReset />} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {rows.map((r) => {
              const denom = r.break_minutes + r.idle_minutes + r.active_minutes || 1;
              return (
                <div key={r.employee_id} className={[VISUAL.card, 'p-5'].join(' ')}>
                  <div className="font-medium text-foreground text-[14px] mb-3">{r.employee_name}</div>
                  <TwinProgressBars
                    primary={{ label: `Break · ${fmtMinutes(r.break_minutes)}`, pct: (r.break_minutes / denom) * 100 }}
                    secondary={{ label: `Idle · ${fmtMinutes(r.idle_minutes)}`, pct: (r.idle_minutes / denom) * 100 }}
                  />
                </div>
              );
            })}
          </div>

          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <DataTable
              rows={rows}
              rowKey={(r) => r.employee_id}
              columns={[
                { key: 'employee', header: 'Employee', cell: (r) => <span className="font-medium text-foreground">{r.employee_name}</span> },
                { key: 'break_count', header: 'Breaks', cell: (r) => r.break_count },
                { key: 'break_minutes', header: 'Break time', cell: (r) => fmtMinutes(r.break_minutes) },
                { key: 'idle_count', header: 'Idle spells', cell: (r) => r.idle_count },
                { key: 'idle_minutes', header: 'Idle time', cell: (r) => fmtMinutes(r.idle_minutes) },
                { key: 'active_minutes', header: 'Active', cell: (r) => fmtMinutes(r.active_minutes) },
              ]}
            />
          </div>
        </>
      )}
    </ReportShell>
  );
}
